import React from 'react';
import { getAvatarVariantUrl } from '../../utils/avatarVariants';
import { formatNutritionNumber } from './formatNutritionNumber';
import { getNutritionScorePresentation } from './nutritionScorePresentation';
import { getWorkoutProgressPresentation } from './workoutProgressPresentation';
import './DojoNutritionIndicators.css';

const FALLBACK_STATES = {
  'on-target': { label: 'In range', symbol: '✓' },
  building: { label: 'Building', symbol: '…' },
  below: { label: 'Below', symbol: '↓' },
  above: { label: 'Above', symbol: '↑' },
  unavailable: { label: 'Unknown', symbol: '?' },
  done: { label: 'Done', symbol: '✓' },
  active: { label: 'Active', symbol: '●' },
  partial: { label: 'In progress', symbol: '…' },
  pending: { label: 'Pending', symbol: '…' },
  missed: { label: 'Missed', symbol: '!' },
  rest: { label: 'Rest', symbol: '☾' },
};

export default function DojoMemberStatusSelector({
  members = [], selectedId, onSelect, label = 'Activity', nutrition = false, workout = false,
}) {
  if (!members.length) return null;
  return (
    <div className={`dojo-member-status ${nutrition ? 'dojo-nutrition' : ''} ${workout ? 'dojo-workout-status' : ''}`} role="group" aria-label={`${label} member status`}>
      <div className={`dojo-member-status-rail ${nutrition ? 'dojo-nutrition-rail' : ''}`}>
        {members.map(member => {
          const name = member.isSelf ? 'You' : member.user.display_name || 'Member';
          const fallback = FALLBACK_STATES[member.status] || FALLBACK_STATES.unavailable;
          const stateLabel = member.label || fallback.label;
          const symbol = member.symbol || fallback.symbol;
          const avatar = getAvatarVariantUrl(member.user, 'sm');
          const scorePresentation = nutrition
            ? getNutritionScorePresentation(member)
            : null;
          const workoutPresentation = workout
            ? getWorkoutProgressPresentation(member)
            : null;
          const hasWorkoutScore = Number.isFinite(member.workoutScore) && member.workoutScore >= 0;
          const exactWorkoutScore = hasWorkoutScore ? Math.round(member.workoutScore).toLocaleString('en-US') : null;
          const workoutScoreLabel = hasWorkoutScore ? `workout score ${exactWorkoutScore}` : 'workout score unavailable';
          const visibleWorkoutScore = hasWorkoutScore
            ? `+${member.workoutScore >= 1000000 ? new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(member.workoutScore) : exactWorkoutScore}`
            : '—';
          const progressPresentation = scorePresentation || workoutPresentation;
          const progress = progressPresentation?.progress;
          const scoreLabel = !nutrition || progress == null
            ? ''
            : `, ${formatNutritionNumber(member.score)}% on target`;
          return <button
            key={member.id}
            type="button"
            className={`dojo-member-status-item ${nutrition ? 'dojo-nutrition-member' : ''} is-${member.status}`}
            aria-label={`${name}: ${stateLabel}${scoreLabel}${workout ? `, ${workoutScoreLabel}` : ''}${workout && member.status === 'missed' ? ', Missed activity' : ''}`}
            aria-pressed={selectedId === member.id}
            onClick={() => onSelect?.(member.id)}
          >
            <span className={`dojo-member-status-portrait ${progressPresentation ? 'dojo-progress-portrait' : ''} ${nutrition ? 'dojo-nutrition-portrait' : ''} ${workout ? 'dojo-workout-portrait' : ''}`}>
              {progressPresentation ? <>
                <svg className={`dojo-avatar-progress ${nutrition ? 'dojo-nutrition-progress' : 'dojo-workout-progress'}`} viewBox="0 0 40 40" aria-hidden="true">
                  <circle className="dojo-avatar-progress-track" cx="20" cy="20" r="17" pathLength="100" />
                  <circle
                    className={`dojo-avatar-progress-value ${nutrition ? 'dojo-nutrition-progress-value' : 'dojo-workout-progress-value'}`}
                    cx="20"
                    cy="20"
                    r="17"
                    pathLength="100"
                    stroke={progressPresentation.color}
                    strokeDasharray={`${progress ?? 0} ${100 - (progress ?? 0)}`}
                  />
                </svg>
                <span className="dojo-progress-avatar">
                  {avatar ? <img src={avatar} alt="" loading="lazy" /> : <span>{(member.user.display_name || '?').slice(0, 1)}</span>}
                </span>
              </> : (avatar ? <img src={avatar} alt="" loading="lazy" /> : <span>{(member.user.display_name || '?').slice(0, 1)}</span>)}
              <span className={`dojo-member-status-symbol ${nutrition ? 'dojo-nutrition-symbol' : ''}`} aria-hidden="true">{symbol}</span>
            </span>
            <span className={`dojo-member-status-name ${nutrition ? 'dojo-nutrition-name' : ''}`}>{name}</span>
            {workout && <span className="dojo-workout-score" title={workoutScoreLabel}>{visibleWorkoutScore}</span>}
            <span className={`dojo-member-status-state ${nutrition ? 'dojo-nutrition-state' : ''}`} style={workoutPresentation ? { color: workoutPresentation.color } : undefined}>{stateLabel}</span>
          </button>;
        })}
      </div>
    </div>
  );
}
